import React, { useEffect, useState } from 'react';

import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';

import { isEqual } from 'lodash';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { GLOBAL_TERRITORY } from 'src/constants/context';
import { IS_REQUIRED } from 'src/constants/errorMessages';
import { LOCALE_EN_CA } from 'src/constants/locale';
import { AppContext } from 'src/contexts/appContext';
import { initialBasePlanLaunchDateState } from 'src/data/BasePlanData';
import { IBasePlan, IBasePlanLaunchDate } from 'src/types/IBasePlanLaunchDate';
import { validateExactLength, validateRequired } from 'src/util/validationsUtil';

import { BasePlanIdSelect } from '../../BasePlanIdSelect';

export interface IBasePlanLaunchDateDetailProps {
    basePlanLaunchDate: IBasePlanLaunchDate;
    setBasePlanLaunchDate: React.Dispatch<React.SetStateAction<IBasePlanLaunchDate>>;
    onNext: () => void;
    onCancel: () => void;
    isEditMode: boolean;
}

export const BasePlanLaunchDateDetails = ({
    basePlanLaunchDate,
    setBasePlanLaunchDate,
    onNext,
    onCancel,
    isEditMode
}: IBasePlanLaunchDateDetailProps) => {
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
    const [isFormValid, setIsFormValid] = useState(false);
    const { metadata } = React.useContext(AppContext);
    const territories = Object.values(metadata.territoryMapping).filter(
        (territory) => territory.territoryCode !== GLOBAL_TERRITORY.NAME
    );

    useEffect(() => {
        setIsFormValid(checkFormValidity());
    }, [basePlanLaunchDate, errorMessages]);

    const handleOnChange = (field: string, value: string) => {
        setBasePlanLaunchDate((prevState) => ({
            ...prevState,
            [field]: value
        }));
    };

    const handleBasePlanChange = (basePlan: IBasePlan) => {
        setBasePlanLaunchDate((prevState) => ({
            ...prevState,
            basePlanName: basePlan.basePlanName || '',
            basePlanId: basePlan.basePlanId
        }));
    };

    const checkFormValidity = () => {
        const requiredFields = ['basePlanName', 'territoryIds', 'startDate'];

        for (const field of requiredFields) {
            if (field === 'territoryIds' && basePlanLaunchDate.territoryIds.length < 1) {
                return false;
            } else if (!basePlanLaunchDate[field]) {
                return false;
            }
        }

        return isEqual(errorMessages, {}) && !isEqual(basePlanLaunchDate, initialBasePlanLaunchDateState);
    };

    const validateField = (field: string) => {
        let errorMessage = '';

        switch (field) {
            case 'basePlanName':
                if (!validateRequired(basePlanLaunchDate.basePlanName)) {
                    errorMessage = IS_REQUIRED;
                }
                break;
            case 'startDate':
                if (!validateRequired(basePlanLaunchDate.startDate)) {
                    errorMessage = IS_REQUIRED;
                }
                break;
            case 'territoryIds':
                if (basePlanLaunchDate.territoryIds.length < 1) {
                    errorMessage = IS_REQUIRED;
                }
        }

        if ((field === 'startDate' || field === 'endDate') && !errorMessage) {
            let endDateErrorMessage = '';
            const startDate = basePlanLaunchDate.startDate?.replaceAll('-', '');
            const endDate = basePlanLaunchDate.endDate?.replaceAll('-', '');
            if (endDate && validateExactLength(endDate, 8) && endDate <= startDate) {
                endDateErrorMessage = 'Mapping end date must be after mapping start date';
            }
            setErrorMessages((prevState) => {
                const newState = { ...prevState };
                if (!endDateErrorMessage) {
                    delete newState.endDate;
                } else {
                    newState.endDate = endDateErrorMessage;
                }
                return newState;
            });
            if (field === 'endDate') return;
        }

        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            if (errorMessage === '') {
                delete newState[field];
            } else {
                newState[field] = errorMessage;
            }
            return newState;
        });
    };

    return (
        <>
            <Heading level={1}>{isEditMode ? 'Edit details' : 'Plan launch date details'}</Heading>
            <Column spacingInset="450 none 500 none" spacing="450">
                <BasePlanIdSelect
                    basePlanId={basePlanLaunchDate.basePlanId}
                    onChange={handleBasePlanChange}
                    validateField={() => validateField('basePlanName')}
                    errorMessage={errorMessages.basePlanName}
                    disabled={isEditMode}
                />
                <Select
                    label="Territory *"
                    value={isEditMode ? basePlanLaunchDate.territoryIds[0] : basePlanLaunchDate.territoryIds}
                    onChange={(value) => handleOnChange('territoryIds', isEditMode ? [value] : value)}
                    onBlur={() => validateField('territoryIds')}
                    error={!!errorMessages.territoryIds}
                    errorMessage={errorMessages.territoryIds}
                    size="xlarge"
                >
                    {territories.map((territory) => (
                        <SelectOption
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            value={territory.territoryId!}
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            label={territory.territoryCode!}
                            key={territory.territoryId}
                        >
                            {() => <TerritoryFlag territoryCode={territory.territoryCode!} />}
                        </SelectOption>
                    ))}
                </Select>
                <Row widths="fill" alignmentVertical="top">
                    <DatePicker
                        label="Start date *"
                        value={basePlanLaunchDate.startDate}
                        onChange={(value) => handleOnChange('startDate', value)}
                        onBlur={() => validateField('startDate')}
                        error={!!errorMessages.startDate}
                        errorMessage={errorMessages.startDate}
                        locale={LOCALE_EN_CA}
                        size="xlarge"
                    />
                    <DatePicker
                        label="End date"
                        value={basePlanLaunchDate.endDate}
                        onChange={(value) => handleOnChange('endDate', value)}
                        onBlur={() => validateField('endDate')}
                        error={!!errorMessages.endDate}
                        errorMessage={errorMessages.endDate}
                        locale={LOCALE_EN_CA}
                        size="xlarge"
                    />
                </Row>
            </Column>
            <Row alignmentHorizontal="end">
                <Button type="link" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="primary" onClick={onNext} disabled={!isFormValid}>
                    Next
                </Button>
            </Row>
        </>
    );
};
